import { memo, useEffect } from "react";

import { trustArcNoticeUrl } from "~/hec/config/trustarc";
import { trackAbTastyEvent } from "~/lib/track-abtasty-event.client";

import type { ClickSource } from "./click-source";
import { pushDataLayer } from "./data-layer.client";
import {
  type Ga4ProductDetailItem,
  buildGa4ProductDetailEcommerce,
} from "./ga4-product-detail";
import {
  INITIAL_NAVIGATION_ORDINAL,
  INITIAL_PAGE_CONTEXT_EVENT,
  type PageContext,
} from "./page-context";
import { safeJsonForScript } from "./safe-json";

/**
 * SSR inline `window.__FLAGSHIP_VID` global — the AB Tasty Universal Collect
 * visitor id, read by client-side hit senders so PAGEVIEW/TRANSACTION/ITEM
 * hits carry the same `vid` the Flagship SDK bucketed the visitor under
 * server-side (see `resolveAbTastyHitIdentity`).
 *
 * Renders in the contiguous inline-script run at the TOP of <body> (see
 * root.tsx), right after `BuilderUserAttributesInlineScript` (COM-101) —
 * same reasoning as `ConsentDefaultsInlineScript`:
 * React 19 Float hoisting has no positional head script to shift, and no 3p
 * script can inject ahead of it in <head>. It has no `dataLayer` dependency,
 * so it's safe to sit ahead of `ConsentDefaultsInlineScript`; keep the run
 * contiguous — no hoistable <link>/<meta> between them.
 *
 * Skips render when no vid resolved (bot traffic, or first byte before any
 * ajs_anonymous_id cookie has ever been set).
 */
export const FlagshipVidInlineScript = memo(function FlagshipVidInlineScript({
  vid,
  nonce,
}: {
  vid: string | null;
  nonce?: string;
}) {
  if (!vid) return null;
  return (
    <script
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.__FLAGSHIP_VID=${safeJsonForScript(vid)};`,
      }}
    />
  );
});

/**
 * SSR inline `window.__PZ_HITS` config — enables the D6 client hit-sender
 * (`abtasty-client-hits.client.ts`). The root loader passes `config` ONLY
 * when `WORKERS_ABTASTY_MODE` is on for a non-bot request; null ⇒ nothing
 * renders ⇒ every sender call no-ops. The legacy `try.abtasty.com` tag rides
 * its OWN switch (§5.5 re-amendment 2026-09-04) and may run ALONGSIDE the
 * sender — coexistence is the intended posture (separate accounts/visitor
 * ids/endpoints; the two share no cookies or wire).
 * Sits directly after `FlagshipVidInlineScript` in the contiguous inline-run
 * — the sender reads both globals, and neither has a dataLayer dependency.
 *
 * FIRST PAINT ONLY: React never re-executes an inline script whose contents
 * change, so post-hydration identity changes (SPA login/logout) are synced
 * by `syncPzHitsConfig` via `AbTastyPageviewTracker`'s effect — this
 * component and that effect are a pair.
 */
export const PzHitsConfigInlineScript = memo(function PzHitsConfigInlineScript({
  config,
  nonce,
}: {
  config: { cid: string; cuid?: string } | null;
  nonce?: string;
}) {
  if (!config?.cid) return null;
  return (
    <script
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.__PZ_HITS=${safeJsonForScript(config)};`,
      }}
    />
  );
});

/**
 * SSR inline `gtag('consent','default',{...})` block. Must execute BEFORE the
 * GTM container script so TrustArc's later `gtag('consent','update',...)` calls
 * have somewhere to land. SFRA-parity 8-key payload mirrors
 * `org_hec/cartridge/templates/default/common/scripts.isml:11-24`.
 *
 * Rendered as the FIRST of a contiguous run of inline scripts at the TOP of
 * <body> (see root.tsx), NOT in <head>: keeping them out of the head means
 * React 19 Float hoisting has no positional head script to shift (that shift
 * tripped a structural hydration mismatch). Keep them contiguous — no hoistable
 * <link>/<meta> between them.
 *
 * SFCC-11006 (ANALYTICS-02).
 */
export const ConsentDefaultsInlineScript = memo(
  function ConsentDefaultsInlineScript({ nonce }: { nonce?: string }) {
    return (
      <script
        suppressHydrationWarning
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html:
            "window.dataLayer=window.dataLayer||[];" +
            "function gtag(){dataLayer.push(arguments);}" +
            "gtag('consent','default',{" +
            "'analytics_storage':'granted'," +
            "'ad_storage':'granted'," +
            "'ad_personalization':'denied'," +
            "'ad_user_data':'denied'," +
            "'functionality_storage':'granted'," +
            "'personalization_storage':'granted'," +
            "'security_storage':'granted'," +
            "'wait_for_update':500" +
            "});",
        }}
      />
    );
  },
);

/**
 * COM-184: SSR inline bootstrap that injects the TrustArc consent-notice
 * script at body parse-time — the same createElement pattern as the GTM
 * container IIFE below, for the same reason (an inline positional script in
 * the contiguous body run; a literal `<script async src>` would be a React 19
 * hoistable and get lifted out of the run on hydrate).
 *
 * Why this exists: the TrustArc script used to be injected exclusively by the
 * Builder `TrustArcBanner` block's `useEffect` — i.e. AFTER download + parse +
 * hydration of the whole React bundle. Under 4x CPU throttle that put the
 * banner's first paint 19–20s out, and the banner text IS the homepage LCP
 * element. Injecting here starts the fetch as soon as the top of `<body>`
 * parses, ~as early as a hand-written head tag, while:
 *
 *  - preserving CONSENT-FIRST ordering by construction: this renders strictly
 *    AFTER `ConsentDefaultsInlineScript` in the same parse-ordered inline run,
 *    so `gtag('consent','default',…)` has always executed before the TrustArc
 *    script (async, needs a network round-trip) can possibly run its
 *    `gtag('consent','update',…)` — the analytics-foundation.md invariant;
 *  - staying async (`s.async=true`) — NOT render-blocking; FCP is unaffected;
 *  - hinting the fetch up the priority ladder (`fetchpriority=high`) since
 *    async-injected scripts default to Low priority in Chromium and the
 *    banner paint is the LCP;
 *  - deduping with the Builder block via `id="trustarc"` — the block's
 *    `getElementById("trustarc")` guard makes its useEffect a no-op when this
 *    bootstrap already ran (the block stays as the client-side fallback for
 *    the Builder editor and any site not mapped in TRUSTARC_DOMAINS).
 *
 * Skips render when the site has no TrustArc domain mapping (WDH) or in the
 * Builder editor (`domain` is passed as null there — the banner overlay would
 * fight the visual editor, same suppression as GTM/AB Tasty/Bloomreach).
 */
export const TrustArcBootstrapInlineScript = memo(
  function TrustArcBootstrapInlineScript({
    domain,
    nonce,
  }: {
    domain?: string | null;
    nonce?: string;
  }) {
    if (!domain) return null;
    const src = safeJsonForScript(trustArcNoticeUrl(domain));
    const nonceExpr = nonce ? `s.nonce=${safeJsonForScript(nonce)};` : "";
    return (
      <script
        suppressHydrationWarning
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html:
            "(function(d){if(d.getElementById('trustarc'))return;" +
            "var s=d.createElement('script');s.id='trustarc';" +
            "s.async=true;s.setAttribute('fetchpriority','high');" +
            nonceExpr +
            `s.src=${src};d.head.appendChild(s);})(document);`,
        }}
      />
    );
  },
);

/**
 * Standard GTM container snippet — the `gtm.start` IIFE that async-loads
 * `gtm.js?id=...`. Mirrors SFRA `gtm/tracking.isml:5-12`, including the
 * `registered` boolean pushed immediately before `gtm.start` so GTM audience
 * segmentation by auth state works on first page load. Skips render when
 * `containerId` is empty (unmapped site/instance).
 *
 * These inline analytics scripts render at the top of <body> (see root.tsx),
 * NOT in <head>. The `dns-prefetch` <link> that used to live here stays in
 * <head>'s link block (it's hoistable): when it sat between the consent inline
 * script and this IIFE in the head, React 19 hoisted it out on the client and
 * shifted the positional scripts after it — the structural hydration mismatch.
 * Keep the inline scripts a contiguous run with no hoistable between them.
 *
 * SFCC-11006 (ANALYTICS-01) + SFCC-11025 (ANALYTICS-35).
 */
export const GtmContainerScript = memo(function GtmContainerScript({
  containerId,
  isRegistered,
  nonce,
}: {
  containerId: string;
  isRegistered: boolean;
  nonce?: string;
}) {
  if (!containerId) return null;
  const id = safeJsonForScript(containerId);
  // `isRegistered` is a server boolean; `JSON.stringify` emits the literal
  // `true`/`false` with no attacker-controllable bytes.
  const registered = JSON.stringify(Boolean(isRegistered));
  return (
    <script
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html:
          "(function(w,d,s,l,i){w[l]=w[l]||[];" +
          `w[l].push({'registered':${registered}});` +
          "w[l].push({'gtm.start':" +
          "new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0]," +
          "j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=" +
          "'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);" +
          `})(window,document,'script','dataLayer',${id});`,
      }}
    />
  );
});

/**
 * COM-162: hard cap on the anti-flicker body-hide used by
 * `AbTastyAsyncTagInlineScript`. The page is revealed when the AB Tasty tag
 * finishes loading/executing (onload/onerror) or when this many ms elapse —
 * whichever comes first. 500 ms is the cap the ticket's sanctioned option
 * names ("async + capped anti-flicker (e.g. 500ms body-hide)").
 */
export const AB_TASTY_ANTIFLICKER_CAP_MS = 500;

/**
 * COM-162: async AB Tasty tag behind a bounded anti-flicker body-hide —
 * rendered ONLY when `WORKERS_ABTASTY_ASYNC_MODE=on` (fail-closed; the flag
 * off path renders the original synchronous `<script src>` in root.tsx,
 * byte-identical to the shipped SFCC-11023 / ANALYTICS-31 behavior).
 *
 * What the inline script does, in parse order at the tag's body-top position:
 *
 *  1. hides the page: `document.body.style.opacity = 0 !important` (the body
 *     element exists — this script is a child of `<body>`; body carries
 *     `suppressHydrationWarning`, so the transient style attribute can't trip
 *     a hydration mismatch). Opacity-hidden content is not painted, so the
 *     experiment-targeting window stays flicker-free like the sync tag.
 *  2. arms the reveal: onload/onerror of the AB Tasty script OR the
 *     `AB_TASTY_ANTIFLICKER_CAP_MS` timeout, whichever first. The tag can
 *     therefore never hide the page longer than the cap — a hung
 *     `try.abtasty.com` degrades to a 500 ms delay instead of today's
 *     unbounded synchronous fetch+exec parser block (~800 ms measured healthy,
 *     unbounded when the vendor is slow).
 *  3. injects the tag: same createElement pattern as
 *     `TrustArcBootstrapInlineScript` above and for the same reason — a
 *     literal `<script async src>` here would be a React 19 hoistable and get
 *     lifted out of the contiguous inline run on hydrate. `fetchpriority=high`
 *     because async-injected scripts default to Low priority in Chromium and
 *     the reveal (and any experiment paint) waits on it.
 *
 * Ordering contract (SFCC-11888): the `ecomm_pagetype` dataLayer push renders
 * earlier in the same parse-ordered inline run, so it has always executed
 * before the AB Tasty script — which now evaluates strictly LATER than the
 * sync variant — can read its one-shot snapshot. Async-only makes that
 * ordering safer, never weaker.
 */
export const AbTastyAsyncTagInlineScript = memo(
  function AbTastyAsyncTagInlineScript({
    scriptUrl,
    nonce,
  }: {
    scriptUrl: string;
    nonce?: string;
  }) {
    if (!scriptUrl) return null;
    const src = safeJsonForScript(scriptUrl);
    const nonceExpr = nonce ? `s.nonce=${safeJsonForScript(nonce)};` : "";
    return (
      <script
        suppressHydrationWarning
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html:
            "(function(d){if(d.getElementById('abtasty-tag'))return;" +
            "var b=d.body,done=false;" +
            "b.style.setProperty('opacity','0','important');" +
            "var r=function(){if(done)return;done=true;" +
            "b.style.removeProperty('opacity');};" +
            `setTimeout(r,${AB_TASTY_ANTIFLICKER_CAP_MS});` +
            "var s=d.createElement('script');s.id='abtasty-tag';" +
            "s.async=true;s.setAttribute('fetchpriority','high');" +
            nonceExpr +
            "s.onload=r;s.onerror=r;" +
            `s.src=${src};d.head.appendChild(s);})(document);`,
        }}
      />
    );
  },
);

/**
 * SSR inline `ecomm_pagetype` push (SFCC-11888). Must land in `window.dataLayer`
 * BEFORE the GTM container IIFE and the synchronous AB Tasty script: GA4's
 * `page_view` fires at `gtm.js` and AB Tasty reads a one-shot snapshot at
 * evaluation time — neither picks up a post-hydration push (the useEffect
 * push landed ~1s late, so AB Tasty qualified 0 campaigns and `ecomm_pagetype`
 * was null on ~98.5% of composable GA4 page_view events).
 *
 * Renders inside the contiguous inline-script run at the TOP of <body>
 * (between the consent defaults and the GTM container — see root.tsx). Body
 * position keeps it out of React 19 head hoisting entirely; keep the run
 * contiguous, no hoistable <link>/<meta> between the scripts.
 *
 * `pageType` comes from `usePageTypeForRender` (root.tsx), identical on server
 * and client render, so hydration sees matching markup. On SPA navigation React
 * updates this script's text but browsers never re-execute an inline script —
 * `PageTypeTracker` (root.tsx) owns nav pushes.
 *
 * Skips render when the caller resolved no pageType. As of SFCC-12034 C7 that
 * is no longer the "unported route" case — those now resolve to the `other`
 * bucket — it is the deliberate non-404 error-render carve-out, so a 5xx isn't
 * counted as an ordinary pageview. See `layoutPageType`.
 */
/**
 * COM-482: fills the two per-request values of a shell document's initial
 * page context (see `resolveInBrowser` below). Assigning to keys the
 * serialized context already has keeps the key order of a regular document.
 *
 * The id has the shape of the server mint (`crypto.randomUUID().slice(0, 8)`,
 * `app.server.ts`); `randomUUID` needs a secure context, hence the fallback.
 * The location is origin + path + query: what the request URL was, original
 * query casing included, and never the fragment, which the server never saw.
 */
const RESOLVE_PAGE_CONTEXT_IN_BROWSER =
  "var d=window.crypto&&crypto.randomUUID?crypto.randomUUID().slice(0,8):" +
  '(Math.random().toString(16)+"00000000").slice(2,10);' +
  `c.hec_navigation_id=d+":${INITIAL_NAVIGATION_ORDINAL}";` +
  "c.hec_page_location=location.origin+location.pathname+location.search;";

export const PageTypeInlineScript = memo(function PageTypeInlineScript({
  pageType,
  nonce,
  initialPageContext,
  resolveInBrowser = false,
}: {
  pageType?: string;
  nonce?: string;
  /**
   * COM-585 — the `hec_initial_page_context` message, pre-built by `Layout`.
   * `null` when the contract is suppressed for this request (flag off, site
   * not allowlisted, bot, or the Builder editor), in which case this component
   * behaves exactly as it did before.
   *
   * Emitted from THIS script rather than its own so the two messages can never
   * be separated or reordered: both have to land before the GTM container and
   * the synchronous AB Tasty tag, and a second `<script>` would be one more
   * thing that could drift out of that contiguous run.
   */
  initialPageContext?: PageContext | null;
  /**
   * COM-482 — a document shell is one prerendered document replayed to every
   * guest, so the two per-request values of the context cannot be baked into
   * it: `hec_navigation_id` (its per-document id) and `hec_page_location`.
   * With this set the script mints the id and reads the location in the
   * browser instead, in the same position and the same single push; the
   * caller passes a context whose two values are empty. A hole cannot carry
   * this: it resumes after the prelude's GTM container is already on the
   * wire, and the context has to precede it.
   */
  resolveInBrowser?: boolean;
}) {
  // The existing carve-out: no pageType means a non-404 error render, which is
  // not an ordinary page view. The context contract follows the same rule —
  // publishing one there would put 5xx renders into QA's navigation counts.
  if (!pageType) return null;
  return (
    <script
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html:
          "window.dataLayer=window.dataLayer||[];" +
          // `pageType` (SFCC-11889 C7) is the same value under the key some
          // GTM tags/variables read; `ecomm_pagetype` stays the AB Tasty /
          // remarketing key.
          `window.dataLayer.push({ecomm_pagetype:${safeJsonForScript(pageType)},pageType:${safeJsonForScript(pageType)}});` +
          // COM-585: ONE complete context, after the bare-key push above so a
          // raw-array reader looking for `ecomm_pagetype` still finds it first
          // (the context carries its page type under `hec_page_type` — see the
          // PageContext docblock for why the bare keys are not reused).
          //
          // Serialized ONCE and used twice: pushed as the contract message,
          // and parked on `window.__HEC_PAGE_CTX` so the route-bound event
          // stamping has a context to read from first paint — before any
          // client publisher has mounted. An IIFE keeps the temporary out of
          // global scope.
          //
          // `safeJsonForScript` and not `JSON.stringify`: the context carries
          // a page title and a URL, both of which can contain CMS/SCAPI text,
          // and `JSON.stringify` does not escape a `</script>` byte sequence.
          (initialPageContext
            ? `(function(){var c=${safeJsonForScript(initialPageContext)};` +
              (resolveInBrowser ? RESOLVE_PAGE_CONTEXT_IN_BROWSER : "") +
              "window.__HEC_PAGE_CTX=c;" +
              `window.dataLayer.push(Object.assign({event:${safeJsonForScript(INITIAL_PAGE_CONTEXT_EVENT)}},c));})();`
            : ""),
      }}
    />
  );
});

/**
 * Client-only, data-dependent GTM dataLayer pushes (ProductDetail,
 * ClickSource, HeapId, Identity) + the cnstrcUserId global + the CIO beacon.
 *
 * Why these are client-side and NOT rendered as inline <script>s: rendering
 * data-dependent inline <script>s into the SSR-hydrated tree (alongside the
 * consent/GTM scripts) made hydration fragile (React 19 Float hoisting can
 * shift a positional <script> when a hoistable is interleaved). Pushing
 * imperatively from a useEffect keeps them out of hydration reconciliation
 * entirely, and
 * unlike a React-rendered inline <script> mounted post-hydration, a useEffect
 * actually runs. These land just after the GTM container loads; GTM drains them
 * from its dataLayer queue. The CIO beacon is injected via createElement AFTER
 * window.cnstrcUserId is set (it reads that global on load).
 *
 * `ecomm_pagetype` is NOT pushed here: snapshot consumers (AB Tasty, GA4
 * page_view at gtm.js) need it before the third-party tags evaluate, so it
 * ships as the SSR `PageTypeInlineScript` above (SFCC-11888).
 */
export const DataLayerClientPushes = memo(function DataLayerClientPushes({
  ga4Items,
  clickSource,
  heapId,
  isAuthenticated,
  userId,
  hashedEmail,
  customerLoyalty,
  customerGroups,
  cnstrcUserId,
  beaconBundleName,
  nonce,
}: {
  ga4Items: Ga4ProductDetailItem[];
  clickSource?: ClickSource | null;
  heapId: string | null;
  isAuthenticated: boolean;
  userId: string | null;
  hashedEmail: string | null;
  customerLoyalty: boolean;
  customerGroups: string[];
  cnstrcUserId?: string | null;
  beaconBundleName?: string | null;
  nonce?: string;
}) {
  useEffect(() => {
    const w = window as unknown as { cnstrcUserId?: string };
    // COM-585: these went through a local `dl.push` and so bypassed the one
    // seam that stamps route context. Routed through `pushDataLayer` instead —
    // it lazy-inits the array exactly as the local handle did, and only the
    // route-bound event below is in the stamping allowlist (identity, heapId
    // and click-source are user-scoped, not route-scoped).
    if (ga4Items.length) {
      // Event-level value + currency (SFCC-11889 C1) — GA4 view_item reads
      // them off `ecommerce`, not off the items.
      pushDataLayer({
        event: "GA4_productDetail",
        ecommerce: buildGa4ProductDetailEcommerce(ga4Items),
      });
      // AB Tasty Universal Collect EVENT hit (SFCC-12079) — hard-load/
      // first-paint PDP view. The SPA-navigation case is covered by
      // `ProductDetailTracker`.
      trackAbTastyEvent({ ec: "User Engagement", ea: "view_item" });
    }
    if (clickSource) {
      pushDataLayer({ event: clickSource.event, value: clickSource.value });
    }
    if (heapId) pushDataLayer({ heapId });
    if (isAuthenticated) {
      pushDataLayer({ userId: userId ?? "" });
      pushDataLayer({ hashedEmail: hashedEmail ?? "" });
      pushDataLayer({ customerLoyalty });
    }
    if (customerGroups.length) {
      pushDataLayer({
        customerGroupSalesforce: JSON.stringify(customerGroups),
      });
    }
    if (cnstrcUserId) w.cnstrcUserId = cnstrcUserId;
    if (beaconBundleName) {
      const s = document.createElement("script");
      s.src = `https://cnstrc.com/js/cust/${beaconBundleName}.js`;
      s.defer = true;
      if (nonce) s.nonce = nonce;
      document.head.appendChild(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
});
